import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Chip, CircularProgress, Select, MenuItem, FormControl, InputLabel, Autocomplete
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    Gavel as GavelIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import api from '../services/api';

const ITEMS_PER_PAGE = 50; // Carrega 50 registros por vez

function Processos() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const [processos, setProcessos] = useState([]);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState(''); // Input do usuário (não filtrado)
    const [tipoAcao, setTipoAcao] = useState('');
    const [status, setStatus] = useState('');
    const [tipoBeneficio, setTipoBeneficio] = useState('');
    const [vara, setVara] = useState('');
    const [localizacao, setLocalizacao] = useState('');
    const [situacao, setSituacao] = useState('');
    const [tiposAcao, setTiposAcao] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [tipoBeneficioList, setTipoBeneficioList] = useState([]);
    const [varaList, setVaraList] = useState([]);
    const [localizacaoList, setLocalizacaoList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const observerTarget = useRef(null);

    // Chaves para localStorage
    const FILTROS_STORAGE_KEY = 'processos_filtros';
    const SCROLL_POSITION_KEY = 'processos_scroll_position';
    const [filtrosCarregados, setFiltrosCarregados] = useState(false);

    // Função para salvar filtros no localStorage
    const salvarFiltros = () => {
        const filtros = {
            searchInput,
            tipoAcao,
            status,
            tipoBeneficio,
            vara,
            localizacao,
            situacao
        };
        localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
    };

    // Carrega filtros salvos ao montar o componente
    useEffect(() => {
        try {
            const filtrosSalvos = localStorage.getItem(FILTROS_STORAGE_KEY);
            if (filtrosSalvos) {
                const filtros = JSON.parse(filtrosSalvos);
                setSearchInput(filtros.searchInput || '');
                setTipoAcao(filtros.tipoAcao || '');
                setStatus(filtros.status || '');
                setTipoBeneficio(filtros.tipoBeneficio || '');
                setVara(filtros.vara || '');
                setLocalizacao(filtros.localizacao || '');
                setSituacao(filtros.situacao || '');
            }
        } catch (error) {
            console.error('Erro ao carregar filtros salvos:', error);
        }
        setFiltrosCarregados(true);
        loadTiposAcao();
        loadStatusList();
        loadTipoBeneficioList();
        loadVaraList();
        loadLocalizacaoList();
    }, []);

    // Salva filtros sempre que mudarem (após terem sido carregados)
    useEffect(() => {
        if (filtrosCarregados) {
            salvarFiltros();
        }
    }, [searchInput, tipoAcao, status, tipoBeneficio, vara, localizacao, situacao, filtrosCarregados]);

    // Restaura posição do scroll quando voltar da edição
    useEffect(() => {
        const scrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
        if (scrollPosition && !loading && processos.length > 0) {
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = parseInt(scrollPosition);
                }
                sessionStorage.removeItem(SCROLL_POSITION_KEY);
            }, 100);
        }
    }, [loading, processos.length]);

    // Salva posição do scroll antes de navegar
    const handleNavigate = (path) => {
        if (scrollContainerRef.current) {
            sessionStorage.setItem(SCROLL_POSITION_KEY, scrollContainerRef.current.scrollTop.toString());
        }
        navigate(path);
    };

    const loadTiposAcao = async () => {
        try {
            const response = await api.get('/tipo-acao-lista?ativo=true');
            setTiposAcao(response.data || []);
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                console.error('❌ Backend não está acessível. Verifique se está rodando na porta 5000.');
            } else {
                console.error('Erro ao carregar tipos de ação:', error);
            }
        }
    };

    const loadStatusList = async () => {
        try {
            const response = await api.get('/status-lista?ativo=true');
            setStatusList(response.data || []);
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                console.error('❌ Backend não está acessível. Verifique se está rodando na porta 5000.');
            } else {
                console.error('Erro ao carregar lista de status:', error);
            }
        }
    };

    const loadTipoBeneficioList = async () => {
        try {
            const response = await api.get('/tipo-beneficio-lista?ativo=true');
            setTipoBeneficioList(response.data || []);
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                console.error('❌ Backend não está acessível. Verifique se está rodando na porta 5000.');
            } else {
                console.error('Erro ao carregar lista de tipos de benefício:', error);
            }
        }
    };

    const loadVaraList = async () => {
        try {
            const response = await api.get('/vara-lista?ativo=true');
            setVaraList(response.data || []);
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                console.error('❌ Backend não está acessível. Verifique se está rodando na porta 5000.');
            } else {
                console.error('Erro ao carregar lista de varas:', error);
            }
        }
    };

    const loadLocalizacaoList = async () => {
        try {
            // Busca valores únicos de localização dos processos
            const response = await api.get('/processos?limit=1000');
            const processos = response.data || [];
            const localizacoes = [...new Set(processos.map(p => p.localizacao).filter(Boolean))].sort();
            setLocalizacaoList(localizacoes.map(loc => ({ valor: loc })));
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                console.error('❌ Backend não está acessível. Verifique se está rodando na porta 5000.');
            } else {
                console.error('Erro ao carregar lista de localizações:', error);
            }
        }
    };

    // Debounce para pesquisa - só pesquisa após 3 caracteres e 500ms de inatividade
    useEffect(() => {
        const timer = setTimeout(() => {
            // Só pesquisa se tiver 3 ou mais caracteres, ou se estiver vazio (limpar busca)
            if (searchInput.length === 0 || searchInput.length >= 3) {
                setSearch(searchInput);
            }
        }, 500); // Aguarda 500ms após parar de digitar

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Carrega processos quando os filtros mudarem (após terem sido carregados)
    useEffect(() => {
        if (filtrosCarregados) {
            resetAndLoad();
        }
    }, [search, tipoAcao, status, tipoBeneficio, vara, localizacao, situacao, filtrosCarregados]);

    // Configura o Intersection Observer para detectar quando chegar no final
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadMoreProcessos();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, loadingMore, loading, search]);

    const resetAndLoad = async () => {
        setProcessos([]);
        setOffset(0);
        setHasMore(true);
        await loadProcessos(0, true);
    };

    const loadProcessos = async (currentOffset = 0, isInitial = false) => {
        if (isInitial) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            let url = '/processos';
            const params = [];
            
            if (search) {
                params.push(`search=${encodeURIComponent(search)}`);
            }
            if (tipoAcao) {
                params.push(`tipo_acao=${encodeURIComponent(tipoAcao)}`);
            }
            if (status) {
                params.push(`status=${encodeURIComponent(status)}`);
            }
            if (tipoBeneficio) {
                params.push(`tipo_beneficio=${encodeURIComponent(tipoBeneficio)}`);
            }
            if (vara) {
                params.push(`vara=${encodeURIComponent(vara)}`);
            }
            if (localizacao) {
                params.push(`localizacao=${encodeURIComponent(localizacao)}`);
            }
            if (situacao) {
                params.push(`situacao=${encodeURIComponent(situacao)}`);
            }

            // Sempre usa paginação, mesmo com filtros/busca,
            // para evitar carregar todos os registros de uma vez.
            params.push(`limit=${ITEMS_PER_PAGE}`);
            params.push(`offset=${currentOffset}`);

            url += '?' + params.join('&');
            
            const response = await api.get(url);
            const newProcessos = response.data || [];
            
            if (isInitial) {
                setProcessos(newProcessos);
            } else {
                // Em paginação, sempre adiciona os próximos registros,
                // independentemente de haver filtros/busca.
                setProcessos(prev => [...prev, ...newProcessos]);
            }

            // Define se ainda há mais páginas com base na quantidade retornada
            setHasMore(newProcessos.length === ITEMS_PER_PAGE);
            setOffset(currentOffset + newProcessos.length);
        } catch (error) {
            console.error('Erro ao carregar processos:', error);
            if (isInitial) {
                alert('Erro ao carregar processos: ' + (error.response?.data?.error || error.message));
                setProcessos([]);
            }
        } finally {
            if (isInitial) {
                setLoading(false);
            } else {
                setLoadingMore(false);
            }
        }
    };

    const loadMoreProcessos = () => {
        if (!loadingMore && hasMore) {
            loadProcessos(offset, false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este processo?')) {
            try {
                await api.delete(`/processos/${id}`);
                // Remove o processo da lista local
                setProcessos(prev => prev.filter(p => p.id !== id));
            } catch (error) {
                console.error('Erro ao deletar processo:', error);
                alert('Erro ao deletar processo: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    // Se houver busca, a filtragem já foi feita no backend
    // Se não houver busca, mostra todos os processos carregados
    const processosFiltrados = processos;

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data está no formato YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss, extrai apenas a parte da data
            // Isso evita problemas de timezone
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
                const partes = date.split('T')[0].split(' ')[0].split('-');
                if (partes.length === 3) {
                    // Retorna DD/MM/YYYY
                    return `${partes[2]}/${partes[1]}/${partes[0]}`;
                }
            }
            // Fallback: tenta usar Date (pode ter problema de timezone)
            return new Date(date).toLocaleDateString('pt-BR');
        } catch {
            return date;
        }
    };

    // Função para verificar se um filtro está ativo (diferente do padrão)
    const isFiltroAtivo = (valor, valorPadrao) => {
        if (valor === null || valor === undefined) return false;
        if (typeof valor === 'boolean') return valor !== valorPadrao;
        if (typeof valor === 'string') {
            if (valorPadrao === null || valorPadrao === undefined) return valor !== '';
            return valor !== valorPadrao;
        }
        return valor !== valorPadrao;
    };

    // Estilo para campos de filtro ativos
    const getFiltroAtivoStyle = (valor, valorPadrao) => {
        if (isFiltroAtivo(valor, valorPadrao)) {
            if (theme.palette.mode === 'dark') {
                // Modo escuro: amarelo mais escuro/opaco
                return {
                    backgroundColor: 'rgba(255, 235, 59, 0.15)', // Amarelo opaco
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 235, 59, 0.15)',
                        '& fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.3)',
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.7)',
                        },
                    },
                    '& .MuiInputBase-root': {
                        backgroundColor: 'rgba(255, 235, 59, 0.15)',
                        color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255, 235, 59, 0.8)',
                    },
                    '& .MuiInputBase-input': {
                        color: theme.palette.text.primary,
                    },
                };
            } else {
                // Modo claro: amarelo fraco
                return {
                    backgroundColor: '#fff9c4',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff9c4',
                    },
                    '& .MuiInputBase-root': {
                        backgroundColor: '#fff9c4',
                    }
                };
            }
        }
        return {};
    };

    return (
        <Box>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <GavelIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Processos
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Gerencie todos os processos jurídicos
                </Typography>
            </Box>

            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/processos/novo')}
                    fullWidth={false}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Novo Processo
                </Button>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por número, cliente, status ou tipo de benefício... (mín. 3 caracteres)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ 
                        flex: 1, 
                        minWidth: 200,
                        ...getFiltroAtivoStyle(searchInput, '')
                    }}
                />
                <Box sx={{ 
                    minWidth: 200,
                    ...getFiltroAtivoStyle(status, '')
                }}>
                    <Autocomplete
                        size="small"
                        options={statusList}
                        getOptionLabel={(option) => option?.valor || ''}
                        value={statusList.find((s) => s.valor === status) || null}
                        onChange={(event, newValue) => setStatus(newValue ? newValue.valor : '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Status"
                                placeholder="Digite para pesquisar..."
                            />
                        )}
                    />
                </Box>
                <Box sx={{ 
                    minWidth: 200,
                    ...getFiltroAtivoStyle(tipoBeneficio, '')
                }}>
                    <Autocomplete
                        size="small"
                        options={tipoBeneficioList}
                        getOptionLabel={(option) => option?.valor || ''}
                        value={tipoBeneficioList.find((t) => t.valor === tipoBeneficio) || null}
                        onChange={(event, newValue) => setTipoBeneficio(newValue ? newValue.valor : '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tipo de Benefício"
                                placeholder="Digite para pesquisar..."
                            />
                        )}
                    />
                </Box>
                <FormControl sx={{ 
                    minWidth: 200,
                    ...getFiltroAtivoStyle(tipoAcao, '')
                }}>
                    <InputLabel>Tipo de Ação</InputLabel>
                    <Select
                        value={tipoAcao}
                        onChange={(e) => setTipoAcao(e.target.value)}
                        label="Tipo de Ação"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {tiposAcao.map((tipo) => (
                            <MenuItem key={tipo.id} value={tipo.valor}>
                                {tipo.valor}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ 
                    minWidth: 200,
                    ...getFiltroAtivoStyle(vara, '')
                }}>
                    <Autocomplete
                        size="small"
                        options={varaList}
                        getOptionLabel={(option) => option?.valor || ''}
                        value={varaList.find((v) => v.valor === vara) || null}
                        onChange={(event, newValue) => setVara(newValue ? newValue.valor : '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vara"
                                placeholder="Digite para pesquisar..."
                            />
                        )}
                    />
                </Box>
                <Box sx={{ 
                    minWidth: 200,
                    ...getFiltroAtivoStyle(localizacao, '')
                }}>
                    <Autocomplete
                        size="small"
                        options={localizacaoList}
                        getOptionLabel={(option) => option?.valor || ''}
                        value={localizacaoList.find((l) => l.valor === localizacao) || null}
                        onChange={(event, newValue) => setLocalizacao(newValue ? newValue.valor : '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Localização"
                                placeholder="Digite para pesquisar..."
                            />
                        )}
                    />
                </Box>
                <FormControl
                    sx={{
                        minWidth: 200,
                        ...getFiltroAtivoStyle(situacao, '')
                    }}
                    size="small"
                >
                    <InputLabel>Situação</InputLabel>
                    <Select
                        label="Situação"
                        value={situacao}
                        onChange={(e) => setSituacao(e.target.value)}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="ATIVO">Ativo</MenuItem>
                        <MenuItem value="INATIVO">Inativo</MenuItem>
                        <MenuItem value="ARQUIVADO">Arquivado</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => {
                        setSearchInput('');
                        setSearch('');
                        setTipoAcao('');
                        setStatus('');
                        setTipoBeneficio('');
                        setVara('');
                        setLocalizacao('');
                        setSituacao('');
                    }}
                    size="small"
                    sx={{ minWidth: 'auto' }}
                >
                    Limpar Filtros
                </Button>
            </Box>

            <TableContainer 
                component={Paper} 
                ref={scrollContainerRef}
                sx={{ overflowX: 'auto', maxWidth: '100%', maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}
            >
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <TableRow>
                            <TableCell sx={{ 
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Nº Processo
                            </TableCell>
                            <TableCell sx={{ 
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Cliente
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', md: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', lg: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Tipo Benefício
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', lg: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Vara
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', sm: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Data Distribuição
                            </TableCell>
                            <TableCell sx={{ 
                                align: 'right',
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                color: theme.palette.text.primary,
                                backdropFilter: 'blur(8px)',
                                borderBottom: `2px solid ${theme.palette.divider}`
                            }}>
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress size={24} sx={{ my: 2 }} />
                                </TableCell>
                            </TableRow>
                        ) : processosFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ color: theme.palette.text.primary, py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum processo encontrado
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {processosFiltrados.map((processo) => (
                                <TableRow 
                                    key={processo.id} 
                                    hover
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? 'rgba(255, 255, 255, 0.05)' 
                                                : 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: theme.palette.text.primary }}>
                                                {processo.numero_processo || '-'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                                {formatDate(processo.data_distribuicao)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: theme.palette.text.primary }}>
                                                {processo.cliente_nome || '-'}
                                            </Typography>
                                            <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                <Chip 
                                                    label={processo.status || 'Sem status'} 
                                                    size="small"
                                                    color={processo.status?.includes('MORTO') ? 'default' : 'primary'}
                                                    sx={{
                                                        backgroundColor: theme.palette.mode === 'dark' && processo.status?.includes('MORTO')
                                                            ? 'rgba(255, 255, 255, 0.1)'
                                                            : undefined,
                                                        color: theme.palette.mode === 'dark' && processo.status?.includes('MORTO')
                                                            ? theme.palette.text.primary
                                                            : undefined
                                                    }}
                                                />
                                                {processo.tipo_beneficio && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {processo.tipo_beneficio}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        <Chip 
                                            label={processo.status || 'Sem status'} 
                                            size="small"
                                            color={processo.status?.includes('MORTO') ? 'default' : 'primary'}
                                            sx={{
                                                backgroundColor: theme.palette.mode === 'dark' && processo.status?.includes('MORTO')
                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                    : undefined,
                                                color: theme.palette.mode === 'dark' && processo.status?.includes('MORTO')
                                                    ? theme.palette.text.primary
                                                    : undefined
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, color: theme.palette.text.primary }}>
                                        {processo.tipo_beneficio || '-'}
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, color: theme.palette.text.primary }}>
                                        {processo.vara || '-'}
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: theme.palette.text.primary }}>
                                        {formatDate(processo.data_distribuicao)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            onClick={() => handleNavigate(`/processos/ver/${processo.id}`)}
                                            color="info"
                                            size="small"
                                            title="Ver detalhes"
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleNavigate(`/processos/editar/${processo.id}`)}
                                            color="primary"
                                            size="small"
                                            title="Editar"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleDelete(processo.id)}
                                            color="error"
                                            size="small"
                                            title="Excluir"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                ))}
                                {/* Elemento sentinela para detectar quando chegar no final */}
                                {hasMore && (
                                    <TableRow ref={observerTarget}>
                                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                            {loadingMore && <CircularProgress size={24} />}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default Processos;
